import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Toast from 'react-native-root-toast'
import { ApolloError } from '@apollo/client'
import * as Sentry from '@sentry/react-native'
import { processArticleContent } from './perspective-processor.util'
import { UseFormReturn } from 'react-hook-form'
import {
  ArticleDetailFragmentDoc,
  CreateArticleInput,
  useCheckUniqTitleLazyQuery,
  useCreateArticleMutation,
} from '../../../../../generated/graphql'
import { Cluster } from '../../perspective-cards/types'
import { ArticleEditorRef } from '../../article-editor/article-editor'
import { MessageType } from '../../../../../utils/message-type'
import {
  ERROR_CODES,
  getApolloErrorCode,
} from '../../../../../utils/error-utils'

function updateArticle(cluster: Cluster, cache: any, result: any) {
  const { data } = result
  if (data) {
    cache.modify({
      id: `Cluster:${cluster.id}`,
      fields: {
        articles: (currentArticles: readonly any[]) => {
          const newArticleRef = cache.writeFragment({
            data: data.createArticle,
            fragment: ArticleDetailFragmentDoc,
          })
          return [...currentArticles, newArticleRef]
        },
      },
    })
  }
}

export type FormShape = {
  minCoRank: string
  isAutoTitle: boolean
  title: string
}
export function useArticleCreation(
  cluster: Cluster,
  form: UseFormReturn<FormShape>,
  editorRef: React.RefObject<ArticleEditorRef>,
  onCreatedArticle: (articleId: string) => void
) {
  const { t } = useTranslation('articles')
  const { t: tError } = useTranslation('errors')
  const [loading, setLoading] = useState(false)
  const [checkUniqTitle] = useCheckUniqTitleLazyQuery()
  const [doCreateArticle] = useCreateArticleMutation({
    update: (cache, result) => updateArticle(cluster, cache, result),
  })

  const handleCreateArticle = async (
    originalContent: string,
    originalText: string
  ) => {
    if (!form.getValues('title')) {
      return Toast.show(t("The title can't be empty"), MessageType.error)
    }

    const minCoRank = form.getValues('minCoRank')
    if (minCoRank && +minCoRank < 0) {
      return Toast.show(t('articles:min-co-rank-error'), MessageType.error)
    }

    if (!originalText.trim()) {
      return Toast.show(t("The content can't be empty"), MessageType.warn)
    }

    const finalContent = processArticleContent(originalContent)
    setLoading(true)

    try {
      // Check unique title
      const isUniqTitle = await checkUniqTitle({
        variables: {
          title: form.getValues('title'),
          clusterId: cluster.id,
        },
      })

      if (!isUniqTitle.data?.checkUniqTitle) {
        form.setValue(
          'title',
          form.getValues('title') +
            ' - #' +
            Math.floor(Math.random() * 1000).toString()
        )
      }

      const input: CreateArticleInput = {
        originalContent: finalContent,
        title: form.getValues('title'),
        clusterId: cluster.id,
        isAutoTitle: false,
        minCoRank: +form.getValues('minCoRank'),
      }

      const result = await doCreateArticle({ variables: { input } })
      Toast.show(t('Your article has been created'), MessageType.info)
      const newId = result.data?.createArticle?.id || ''
      onCreatedArticle(newId)
    } catch (e) {
      if (e instanceof ApolloError) {
        const errorCode = getApolloErrorCode(e)
        if (errorCode === '409') {
          return Toast.show(
            tError('create-perspective-conflict'),
            MessageType.error
          )
        }
        if (errorCode === ERROR_CODES.PAYMENT_REQUIRED) {
          return Toast.show(
            tError('node-active-user-limit-reached'),
            MessageType.error
          )
        }

        Toast.show((e as Error).message, MessageType.error)
        console.error(e)
        Sentry.captureException(e)
      }
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    handleCreateArticle,
  }
}
